import {
	ILotInfo,
	IReconciliation,
	ISummaryDetail,
	IVehicleInfo,
	TReconciliationStatusType,
	IReconciliationNoteHistory,
	IReconciliationReason,
	IReconciliationResolution,
} from "@/DataPages/types/inventory/models/reconciliation";
import { GridPreProcessEditCellProps } from "@mui/x-data-grid-pro";

// Types
import { TReconciliationType } from "../types/reconciliation";

// Constants
import { FLEET_VEHICLE_TYPES, ReconciliationStatus } from "../constants/reconciliation";

function filterByProperties({
	rows,
	search,
	vehicleProperties,
	lotProperties,
	reconciliationProperties,
}: {
	rows: IReconciliation[];
	search: string;
	vehicleProperties?: (keyof IVehicleInfo)[];
	lotProperties?: (keyof ILotInfo)[];
	reconciliationProperties?: (keyof IReconciliation)[];
}): IReconciliation[] {
	if (!search?.trim().length) {
		return rows;
	}

	const lowerSearch = search.toLowerCase();

	const filteredRows: IReconciliation[] = [];
	for (const row of rows) {
		let isVehicleMatch = false;
		if (vehicleProperties) {
			isVehicleMatch = filterRowByVehicleProperties(row, vehicleProperties, lowerSearch);
		}

		let isLotMatch = false;
		if (lotProperties) {
			isLotMatch = filterRowByLotProperties(row, lotProperties, lowerSearch);
		}

		let isReconciliationMatch = false;
		if (reconciliationProperties) {
			isReconciliationMatch = filterRowByReconciliationProperties({ row, reconciliationProperties, lowerSearch });
		}

		if (isVehicleMatch || isLotMatch || isReconciliationMatch) {
			filteredRows.push(row);
		}
	}

	return filteredRows;
}

const matchesSearchCriteria = (value: string | number, lowerSearch: string): boolean => {
	if (
		(typeof value === "string" && value.toLowerCase().includes(lowerSearch)) ||
		(typeof value === "number" && String(value).includes(lowerSearch))
	) {
		return true;
	}
	return false;
};

const filterRowByVehicleProperties = (row: IReconciliation, vehicleProperties: (keyof IVehicleInfo)[], lowerSearch: string) => {
	for (const property of vehicleProperties) {
		const fieldValue = row.vehicle[property];
		if (matchesSearchCriteria(fieldValue, lowerSearch)) {
			return true;
		}
	}
	return false;
};

const filterRowByLotProperties = (row: IReconciliation, lotProperties: (keyof ILotInfo)[], lowerSearch: string) => {
	if (!row.lot) {
		return false;
	}

	for (const property of lotProperties) {
		const fieldValue = row.lot[property];
		if (matchesSearchCriteria(fieldValue, lowerSearch)) {
			return true;
		}
	}
	return false;
};

const filterRowByReconciliationProperties = ({
	row,
	reconciliationProperties,
	lowerSearch,
}: {
	row: IReconciliation;
	reconciliationProperties: string[];
	lowerSearch: string;
}) => {
	for (const property of reconciliationProperties) {
		const fieldValue = row[property as keyof IReconciliation];
		if ((typeof fieldValue === "string" || typeof fieldValue === "number") && matchesSearchCriteria(fieldValue, lowerSearch)) {
			return true;
		}
	}
	return false;
};

function handleOpenCollapse(amountOfResults: number): boolean {
	return amountOfResults > 0;
}

const buildTextForUnreconciled = (unreconciled: number) => {
	return unreconciled > 0 ? ` (${unreconciled} Unreconciled)` : "";
};

const filterConditions: Record<ReconciliationStatus, Array<(r: IReconciliation) => boolean>> = {
	[ReconciliationStatus.UNITS_ON_LOT]: [(r) => !!r.gpsId],
	[ReconciliationStatus.UNITS_OFF_LOT]: [(r) => !!r.gpsId],
	[ReconciliationStatus.UNITS_ON_SCHEDULE]: [(r) => !!r.dmsId],
	[ReconciliationStatus.ON_SCH_NOT_ON_LOT]: [(r) => !!r.dmsId],
	[ReconciliationStatus.ON_LOT_NOT_ON_SCH]: [(r) => !!r.gpsId, (r) => !r.dmsId],
	[ReconciliationStatus.ON_LOT_NOT_ON_MFG]: [(r) => !!r.gpsId, (r) => !r.dmsId],
	[ReconciliationStatus.ON_MFG_NOT_ON_LOT]: [(r) => !!r.gpsId, (r) => !!r.dmsId],
	[ReconciliationStatus.UNITS_ON_MFG]: [],
	[ReconciliationStatus.OFF_LOT_NOT_ON_SCH]: [],
	[ReconciliationStatus.OFF_LOT_NOT_ON_MFG]: [],
};

function conditionsMet(reconciliation: IReconciliation, conditions: Array<(r: IReconciliation) => boolean>): boolean {
	return conditions.every((condition) => condition(reconciliation));
}

function filterReconciliations(
	reconciliations: IReconciliation[],
	status: string[],
	conditions: ((reconciliation: IReconciliation) => boolean)[]
): IReconciliation[] {
	if (!reconciliations) {
		return [];
	}
	return reconciliations.filter((reconciliation) => status.includes(reconciliation.status) && conditionsMet(reconciliation, conditions));
}

function reconciliationFilterByType(
	reconciliations: IReconciliation[],
	status: string[],
	filterType: ReconciliationStatus
): IReconciliation[] {
	const conditions = filterConditions[filterType] || [];
	return filterReconciliations(reconciliations, status, conditions);
}

function getValueFromData(key: keyof ISummaryDetail, data: Partial<ISummaryDetail>, defaultValue = ""): string | number {
	return data[key] ?? defaultValue;
}

function normalizeValue(input: keyof TReconciliationStatusType) {
	const mapValue: Record<keyof TReconciliationStatusType, { title: string; value: TReconciliationType }> = {
		newVehicleReconciliation: {
			title: "New Vehicle Reconciliation",
			value: "New",
		},
		usedVehicleReconciliation: {
			title: "Pre-Owned Vehicle Reconciliation",
			value: "Used",
		},
	};

	return mapValue[input];
}
const validateReasonField = (params: GridPreProcessEditCellProps, resolutionOptions: IReconciliationResolution[]) => {
	const notesHistory = params.otherFieldsProps?.notes?.value.notes ?? params.row.notes?.notes;
	const hasNotes = notesHistory?.some((note: IReconciliationNoteHistory) => !!note.note);
	const counterpartValue = params.otherFieldsProps?.resolution?.value;
	const isCounterpartValid = resolutionOptions.some((option) => option.resolution === counterpartValue);
	const isError = !params.props.value && !isCounterpartValid && !hasNotes;
	return { ...params.props, error: isError };
};

const validateResolutionField = (params: GridPreProcessEditCellProps, reasonOptions: IReconciliationReason[]) => {
	const notesHistory = params.otherFieldsProps?.notes?.value.notes ?? params.row.notes?.notes;
	const hasNotes = notesHistory?.some((note: IReconciliationNoteHistory) => !!note.note);
	const counterpartValue = params.otherFieldsProps?.reason?.value;
	const isCounterpartValid = reasonOptions.some((option) => option.reason === counterpartValue);
	const isError = !params.props.value && !isCounterpartValid && !hasNotes;
	return { ...params.props, error: isError };
};

const validateNotesField = (
	params: GridPreProcessEditCellProps,
	reasonOptions: IReconciliationReason[],
	resolutionOptions: IReconciliationResolution[]
) => {
	const hasNotes = params.props.value?.notes?.some((noteHistory: IReconciliationNoteHistory) => !!noteHistory.note);
	const reason = params.otherFieldsProps?.reason?.value;
	const isValidReason = reasonOptions.some((option) => option.reason === reason);
	const resolution = params.otherFieldsProps?.resolution?.value;
	const isValidResolution = resolutionOptions.some((option) => option.resolution === resolution);
	return { ...params.props, error: !hasNotes && !isValidReason && !isValidResolution };
};

const downloadFiles = (files: { presignedUrl: string; fileName: string }[]) => {
	const ONE_SECOND_MS = 1000;
	files.forEach((file, index) => {
		setTimeout(() => {
			const link = document.createElement("a");
			link.href = file.presignedUrl;
			link.setAttribute("download", file.fileName);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}, index * ONE_SECOND_MS);
	});
};

function getVehicleAmount(filter: string, data: IReconciliation[]): number {
	if (!data?.length) {
		return 0;
	}

	return data.filter((row) => {
		if (filter.toLocaleLowerCase() === "fleet") {
			return FLEET_VEHICLE_TYPES.includes(row.gps?.gpsVehicleType?.toLowerCase() ?? "");
		}
		if (filter.toLowerCase() === "loaners") {
			return row.gps?.gpsVehicleType?.toLowerCase() === "loaner";
		}
		if (filter.toLowerCase() === "pre-owned") {
			return row.vehicle.type.toLowerCase() === "used";
		}
		return row.vehicle.type.toLowerCase() === filter.toLowerCase();
	})?.length;
}

function getFilteredReconciliationData(activeFilters: string[], data: IReconciliation[]): IReconciliation[] {
	if (!activeFilters.length) {
		return data;
	}

	return data.filter((row) => {
		if (activeFilters.includes("Fleet")) {
			return FLEET_VEHICLE_TYPES.includes(row.gps?.gpsVehicleType?.toLowerCase() ?? "");
		}
		if (activeFilters.includes("Loaners")) {
			return row.gps?.gpsVehicleType?.toLowerCase() === "loaner";
		}
		if (activeFilters.includes("Pre-owned")) {
			return activeFilters.includes(row.vehicle.type) || row.vehicle.type.toLowerCase() === "used";
		}
		return activeFilters.includes(row.vehicle.type);
	});
}

export {
	filterByProperties,
	getValueFromData,
	handleOpenCollapse,
	buildTextForUnreconciled,
	reconciliationFilterByType,
	normalizeValue,
	validateReasonField,
	validateResolutionField,
	validateNotesField,
	downloadFiles,
	getVehicleAmount,
	getFilteredReconciliationData,
};
